/**
 * Collapsible list of previously prescribed medications and vaccines for a patient.
 * Deduplicates by product, showing only the most recent treatment per product.
 * Each row has a "+" button to select it for re-prescribing.
 */
import { ChevronDown, Plus, ScrollText } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { PrescribingContext, TreatmentWithRelations } from '@snootsvet/shared'
import { getPrimaryOrder } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/frontend/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { useListPatientTreatments } from '@/frontend/hooks/api/patients/use-list-patient-treatments'
import { cn } from '@/frontend/lib/utils'

import type { FormTreatmentItem } from './treatments.types'
import { toReprescribeItem } from './treatments.utils'

export function PreviousTreatmentsList({
  patientId,
  prescribingContext,
  excludeProductIds,
  excludeCatalogProductIds,
  disabled,
  onSelect,
}: {
  patientId: string
  prescribingContext: PrescribingContext
  /** Product IDs already present — excluded from the list. */
  excludeProductIds?: Set<string>
  /** Catalog product IDs already present — excluded from the list. */
  excludeCatalogProductIds?: Set<string>
  disabled?: boolean
  onSelect: (item: FormTreatmentItem) => void
}) {
  const [open, setOpen] = useState(false)
  const { data: treatments } = useListPatientTreatments(patientId)

  const previousTreatments = useMemo(() => {
    if (!treatments) return []

    const eligible = treatments.filter((t) => {
      // In standalone/remote context, only show non-administered treatments
      if (prescribingContext !== 'clinic_consultation') return !t.administeredInConsult
      // In-consult context shows everything
      return true
    })

    const byProduct = new Map<string, TreatmentWithRelations>()
    for (const t of eligible) {
      const key = t.productId ?? t.catalogProductId ?? t.id
      const existing = byProduct.get(key)
      if (!existing || t.createdAt > existing.createdAt) {
        byProduct.set(key, t)
      }
    }

    return [...byProduct.values()]
      .filter((t) => {
        if (t.productId && excludeProductIds?.has(t.productId)) return false
        if (t.catalogProductId && excludeCatalogProductIds?.has(t.catalogProductId)) return false
        return true
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [treatments, excludeProductIds, excludeCatalogProductIds, prescribingContext])

  if (previousTreatments.length === 0) return null

  return (
    <Collapsible open={open && !disabled} onOpenChange={disabled ? undefined : setOpen}>
      <CollapsibleTrigger
        disabled={disabled}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer select-none py-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronDown className={cn('size-3 transition-transform', !open && '-rotate-90')} />
        Previous prescriptions ({previousTreatments.length})
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border rounded-md divide-y mt-1">
          {previousTreatments.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-3 py-2 gap-2">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-medium truncate flex items-center gap-1.5">
                  {t.isExternal && (
                    <Tooltip>
                      <TooltipTrigger
                        render={<ScrollText className="size-3 shrink-0 text-muted-foreground" />}
                      />
                      <TooltipContent>External prescription</TooltipContent>
                    </Tooltip>
                  )}
                  {t.productInfo.name}
                </span>
                <p className="text-sm text-muted-foreground truncate">
                  {t.productInfo.quantityLabel}
                  {t.instructions ? ` — ${t.instructions}` : ''}
                </p>
                <div className="mt-0.5">
                  <Badge variant="outline" size="sm" className="bg-muted/60 font-normal">
                    {/* A treatment's clinic is the one it was dispensed at, same as
                        the timeline entry resolves it. */}
                    <FormattedDate
                      value={t.createdAt}
                      clinicId={getPrimaryOrder(t.orders)?.clinicId ?? null}
                    />
                  </Badge>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                type="button"
                disabled={disabled}
                onClick={() => onSelect(toReprescribeItem(t, prescribingContext))}
              >
                <Plus className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
